import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { businessService, OsmImportResult, OsmImportJob } from '@/services/business.service'
import { regionService } from '@/services/region.service'
import { useConfirm } from '@/hooks/useConfirm'
import { BusinessStatus } from '@shared/types/entities'
import { ApiError } from '@/lib/api-client'
import { AdminLayout } from '@/components/AdminLayout'

export function BusinessManagementPage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const queryClient = useQueryClient()
  const { confirm, modal: confirmModal } = useConfirm()

  const [selectedStateId, setSelectedStateId] = useState('')
  const [selectedRegionId, setSelectedRegionId] = useState('')
  const [selectedCityId, setSelectedCityId] = useState('')
  const [selectedCityName, setSelectedCityName] = useState('')
  const [importResult, setImportResult] = useState<OsmImportResult | null>(null)
  const [importJob, setImportJob] = useState<OsmImportJob | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [listRegionId, setListRegionId] = useState('')
  const [listCityId, setListCityId] = useState('')
  const [selectedAreaId, setSelectedAreaId] = useState<string>('')
  const [selectedStatus, setSelectedStatus] = useState<BusinessStatus>(BusinessStatus.AVAILABLE)

  // Poll for job completion
  useEffect(() => {
    if (importJob?.status === 'RUNNING' && token) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const status = await businessService.getOsmImportStatus(importJob.jobId, token)
          if (status.status === 'COMPLETED') {
            setImportJob(null)
            setImportResult({
              city: status.city,
              totalFound: status.totalFound || 0,
              imported: status.imported || 0,
              skipped: status.skipped || 0,
              areas: status.areas || [],
            })
            queryClient.invalidateQueries({ queryKey: ['businesses'] })
            queryClient.invalidateQueries({ queryKey: ['areas'] })
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
          } else if (status.status === 'FAILED') {
            setImportJob(null)
            setImportError(status.error || t('admin.importFailed'))
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
          }
        } catch {
          // Keep polling on network errors
        }
      }, 3000)
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
    }
  }, [importJob?.status, importJob?.jobId, token, queryClient])

  // Fetch states
  const { data: statesData } = useQuery({
    queryKey: ['states'],
    queryFn: () => regionService.listStates(token!),
    enabled: !!token,
  })

  // Fetch regions
  const { data: regionsData } = useQuery({
    queryKey: ['regions', selectedStateId],
    queryFn: () => regionService.listRegions(selectedStateId, token!),
    enabled: !!token && !!selectedStateId,
  })

  // Fetch cities for selected region
  const { data: citiesData } = useQuery({
    queryKey: ['cities', selectedRegionId],
    queryFn: () => regionService.listCities({ regionId: selectedRegionId }, token!),
    enabled: !!token && !!selectedRegionId,
  })

  // Fetch cities for business list filter
  const { data: listCitiesData } = useQuery({
    queryKey: ['cities', listRegionId],
    queryFn: () => regionService.listCities({ regionId: listRegionId }, token!),
    enabled: !!token && !!listRegionId,
  })

  // Fetch areas for selected city in business list
  const { data: listAreasData } = useQuery({
    queryKey: ['areas', listCityId],
    queryFn: () => regionService.listAreas({ cityId: listCityId }, token!),
    enabled: !!token && !!listCityId,
  })

  // Fetch businesses with infinite scroll
  const {
    data: businessesData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['businesses', selectedAreaId, selectedStatus],
    queryFn: ({ pageParam }) =>
      businessService.listBusinesses(
        { areaId: selectedAreaId, status: selectedStatus, limit: 50, nextToken: pageParam },
        token!
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: any) => lastPage.nextToken || undefined,
    enabled: !!token && !!selectedAreaId,
  })

  const businesses = businessesData?.pages.flatMap((page: any) => page.businesses || []) || []
  const totalCount = (businessesData?.pages[0] as any)?.totalCount as number | undefined

  // Infinite scroll observer for admin list
  const adminLoadMoreRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!adminLoadMoreRef.current || !hasNextPage || isFetchingNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) fetchNextPage()
      },
      { threshold: 0.1 }
    )
    observer.observe(adminLoadMoreRef.current)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // OSM Import mutation (starts async job)
  const osmImportMutation = useMutation({
    mutationFn: () => businessService.importFromOsm(selectedCityName, selectedRegionId, selectedCityId, token!),
    onSuccess: (job) => {
      setImportResult(null)
      setImportError(null)
      setImportJob(job)
    },
    onError: (error) => {
      setImportError(error instanceof ApiError ? error.message : 'Import konnte nicht gestartet werden')
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (businessId: string) => businessService.deleteBusiness(businessId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (areaId: string) => businessService.bulkDeleteByArea(areaId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
      queryClient.invalidateQueries({ queryKey: ['areas'] })
    },
  })

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.businessManagement')}</h1>
          <p className="text-gray-600">{t('admin.importManageBusinesses')}</p>
        </div>

        {/* OSM Import Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.osmImport')}</h2>
          <p className="text-sm text-gray-600 mb-4">
            {t('admin.osmImportDesc')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.state')}</label>
              <select
                value={selectedStateId}
                onChange={(e) => { setSelectedStateId(e.target.value); setSelectedRegionId(''); setSelectedCityId(''); setSelectedCityName('') }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">{t('admin.selectState')}</option>
                {(statesData as any)?.states?.map((s: any) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.region')}</label>
              <select
                value={selectedRegionId}
                onChange={(e) => { setSelectedRegionId(e.target.value); setSelectedCityId(''); setSelectedCityName('') }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={!selectedStateId}
              >
                <option value="">{t('admin.selectRegion')}</option>
                {(regionsData as any)?.regions?.map((r: any) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.city')}</label>
              <select
                value={selectedCityId}
                onChange={(e) => {
                  setSelectedCityId(e.target.value)
                  const city = (citiesData as any)?.cities?.find((c: any) => c.id === e.target.value)
                  setSelectedCityName(city?.name || '')
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={!selectedRegionId}
              >
                <option value="">{t('admin.selectCity')}</option>
                {(citiesData as any)?.cities?.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={() => osmImportMutation.mutate()}
            disabled={!selectedCityName || !selectedRegionId || !selectedCityId || osmImportMutation.isPending || !!importJob}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {osmImportMutation.isPending ? t('admin.startingImport') : importJob ? t('admin.importRunning') : t('admin.importCity', { city: selectedCityName || '...' })}
          </button>

          {importJob && (
            <div className="mt-4 p-4 bg-blue-50 rounded-md flex items-center gap-3">
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-sm text-blue-700">
                {t('admin.importRunningMessage', { city: importJob.city })}
              </p>
            </div>
          )}

          {importResult && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <h3 className="font-semibold text-green-800 mb-2">{t('admin.importCompleted', { city: importResult.city })}</h3>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">{importResult.totalFound}</div>
                  <div className="text-xs text-green-600">{t('admin.found')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">{importResult.imported}</div>
                  <div className="text-xs text-green-600">{t('admin.imported')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-500">{importResult.skipped}</div>
                  <div className="text-xs text-gray-500">{t('admin.skipped')}</div>
                </div>
              </div>
              {importResult.areas.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-green-700 mb-1">{t('admin.generatedAreas')}</h4>
                  <div className="space-y-1">
                    {importResult.areas.map((area) => (
                      <div key={area.id} className="flex justify-between text-sm">
                        <span className="text-green-800">{area.name}</span>
                        <span className="text-green-600 font-medium">{t('admin.businessCount', { count: area.businessCount })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {importError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{importError}</p>
            </div>
          )}
        </div>

        {/* Business List Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.businesses')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.region')}</label>
              <select
                value={listRegionId}
                onChange={(e) => { setListRegionId(e.target.value); setListCityId(''); setSelectedAreaId('') }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">{t('admin.selectRegion')}</option>
                {(regionsData as any)?.regions?.map((r: any) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.city')}</label>
              <select
                value={listCityId}
                onChange={(e) => { setListCityId(e.target.value); setSelectedAreaId('') }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={!listRegionId}
              >
                <option value="">{t('admin.selectCity')}</option>
                {(listCitiesData as any)?.cities?.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.areas')}</label>
              <select
                value={selectedAreaId}
                onChange={(e) => setSelectedAreaId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={!listCityId}
              >
                <option value="">{t('admin.selectArea')}</option>
                {(listAreasData as any)?.areas?.map((area: any) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('scout.status')}</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as BusinessStatus)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value={BusinessStatus.AVAILABLE}>{t('scout.available')}</option>
                <option value={BusinessStatus.RESERVED}>{t('scout.reserved')}</option>
                <option value={BusinessStatus.VISITED}>{t('scout.visited')}</option>
                <option value={BusinessStatus.REGISTERED}>{t('scout.registered')}</option>
                <option value={BusinessStatus.INACTIVE}>{t('common.inactive')}</option>
              </select>
            </div>
          </div>

          {selectedAreaId && (
            <div className="mt-4 flex items-center gap-4">
              <button
                onClick={async () => {
                  const areaName = (listAreasData as any)?.areas?.find((a: any) => a.id === selectedAreaId)?.name || selectedAreaId
                  const ok = await confirm({
                    title: t('admin.bulkDelete'),
                    message: t('admin.confirmBulkDelete', { area: areaName }),
                    confirmLabel: t('admin.deleteAll'),
                    cancelLabel: t('common.cancel'),
                    variant: 'danger',
                  })
                  if (ok) {
                    bulkDeleteMutation.mutate(selectedAreaId)
                  }
                }}
                disabled={bulkDeleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleteMutation.isPending ? t('admin.deleting') : t('admin.deleteAllInArea')}
              </button>
              {bulkDeleteMutation.isSuccess && (
                <span className="text-sm text-green-600">
                  {t('admin.deletedCount', { count: (bulkDeleteMutation.data as any)?.deleted || 0 })}
                </span>
              )}
              {bulkDeleteMutation.isError && (
                <span className="text-sm text-red-600">
                  {bulkDeleteMutation.error instanceof ApiError ? bulkDeleteMutation.error.message : t('admin.deleteFailed')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Business List */}
        <div className="bg-white rounded-lg shadow">
          <div className="divide-y divide-gray-200">
            {!selectedAreaId ? (
              <div className="p-4 text-center text-gray-600">{t('scout.selectAreaFirst')}</div>
            ) : isLoading ? (
              <div className="p-4 text-center text-gray-600">{t('common.loading')}</div>
            ) : businesses.length > 0 ? (
              businesses.map((business: any) => (
                <div key={business.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{business.name}</div>
                      <div className="text-sm text-gray-600">{business.address}</div>
                      <div className="text-sm text-gray-500">
                        {business.city} {business.postalCode}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {business.category && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                            {business.category}
                          </span>
                        )}
                        {business.phone && (
                          <span className="text-xs text-gray-500">{business.phone}</span>
                        )}
                        {business.website && (
                          <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                            {t('admin.website')}
                          </a>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        const ok = await confirm({
                          title: t('common.delete'),
                          message: t('admin.confirmDelete'),
                          confirmLabel: t('common.delete'),
                          cancelLabel: t('common.cancel'),
                          variant: 'danger',
                        })
                        if (ok) {
                          deleteMutation.mutate(business.id)
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      className="ml-4 px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-600">{t('admin.noBusinessesFound')}</div>
            )}
          </div>
          {businesses.length > 0 && (
            <div className="p-4 border-t border-gray-200 text-center text-sm text-gray-500">
              {businesses.length}{totalCount !== undefined && ` / ${totalCount}`} {t('admin.businesses')}
            </div>
          )}
          <div ref={adminLoadMoreRef} className="py-2 text-center">
            {isFetchingNextPage && (
              <span className="text-sm text-gray-500">{t('common.loading')}</span>
            )}
          </div>
        </div>
      </div>
      {confirmModal}
    </AdminLayout>
  )
}
